import { UntypedFormGroup } from '@angular/forms';
import { DayJSService } from 'src/app/services/dayJS/dayJS.service';
import {
  PatientInfoV2,
  PatientInfoWithConsent,
  UpdatedPatientInfo,
} from 'src/app/modules/registration/services/models/patient-info.model';
import { LanguageService } from 'src/app/services/language/language.service';
import { AnalyticsMode } from 'src/graphql/types/api.types';
import { UserRegistrationService } from '../user-registration.service';

export interface BaseRestPatientInfoV2 {
  getPatientInfoV2(): PatientInfoV2;
  getUpdatedPatientInfo(): UpdatedPatientInfo;
}

export interface BaseRestPatientInfoWithProfile {
  getPatientInfoForConsents(): PatientInfoWithConsent;
}

export class RestPatientInfoWithConsents implements BaseRestPatientInfoWithProfile {
  private registrationForm: UntypedFormGroup;

  constructor(
    registrationForm: UntypedFormGroup,
    private requireAnalyticsMode: boolean,
    private optInAnalyticsMode: AnalyticsMode,
    private optOutAnalyticsMode: AnalyticsMode,
  ) {
    this.registrationForm = registrationForm;
  }

  public getPatientInfoForConsents(): PatientInfoWithConsent {
    const analyticsFormValue: boolean = this.registrationForm.get(
      'patientLegalConsents.analyticsMode',
    ).value;
    const analyticsMode = this.requireAnalyticsMode
      ? analyticsFormValue
        ? this.optInAnalyticsMode
        : this.optOutAnalyticsMode
      : undefined;

    return new PatientInfoWithConsent(
      this.registrationForm.get('patientLegalConsents.keepMeInformed').value,
      this.registrationForm.get('patientLegalConsents.shareDetailsWithProviderOptIn').value,
      analyticsMode,
      this.registrationForm.get('patientLegalConsents.processHealthDataOptIn').value,
    );
  }
}

export class RestPatientInfo implements BaseRestPatientInfoV2 {
  private registrationForm: UntypedFormGroup;

  constructor(
    registrationForm: UntypedFormGroup,
    private dayJSService: DayJSService,
    private languageService: LanguageService,
    private userRegistrationService: UserRegistrationService,
    private requireAnalyticsMode: boolean,
    private optInAnalyticsMode: AnalyticsMode,
    private optOutAnalyticsMode: AnalyticsMode,
  ) {
    this.registrationForm = registrationForm;
  }

  //Utility method to check if a value is populated
  private isPopulated(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (typeof value === 'number') {
      return !isNaN(value);
    }
    return true;
  }

  public getPatientInfoV2(): PatientInfoV2 {
    const form = this.registrationForm;
    // Helper function for non-numeric fields.
    // If a value is not populated, set it as undefined so it doesn't post to database
    const getValue = (path: string) => {
      const value = form.get(path).value;
      return this.isPopulated(value) ? value : undefined;
    };

    // Helper function for numeric fields
    // If a value is not populated, set it as undefined so it doesn't post to database
    const getNumericValue = (path: string) => {
      const value = getValue(path);
      return value !== undefined ? Number(value) : undefined;
    };

    const patientCountry = this.registrationForm.get('patientPersonalInfo.country').value;
    const analyticsFormValue: boolean = this.registrationForm.get(
      'patientLegalConsents.analyticsMode',
    ).value;
    const analyticsMode = this.requireAnalyticsMode
      ? analyticsFormValue
        ? this.optInAnalyticsMode
        : this.optOutAnalyticsMode
      : undefined;

    return new PatientInfoV2(
      !!this.registrationForm.get('patientLegalConsents.termsOfUseConsent').value,
      this.getFormattedBirthDay(),
      (this.registrationForm.get('patientPersonalInfo.signUpGroup.email').value as string).trim(),
      patientCountry,
      this.languageService.getCurrent2LettersLanguage(),
      (this.registrationForm.get('patientPersonalInfo.lastName').value as string).trim(),
      (this.registrationForm.get('patientPersonalInfo.firstName').value as string).trim(),
      this.registrationForm.get('patientPersonalInfo.gender').value,
      this.registrationForm.get('patientPersonalInfo.timeZone').value,
      !!this.registrationForm.get('patientLegalConsents.keepMeInformed').value,
      !!this.registrationForm.get('patientLegalConsents.shareDetailsWithProviderOptIn').value,
      // set optional parameters to undefined if empty
      getValue('patientPersonalInfo.furiganaFamilyName')?.trim(),
      getValue('patientPersonalInfo.furiganaGivenName')?.trim(),
      analyticsMode,
      !!getValue('patientLegalConsents.processHealthDataOptIn'),
      this.isPopulated(this.userRegistrationService.serialNumber)
        ? this.userRegistrationService.serialNumber.toString()
        : undefined,
      this.isPopulated(this.userRegistrationService.deviceNumber)
        ? this.userRegistrationService.deviceNumber.toString()
        : undefined,
      // no getValue on sleepTestType because empty sleepTestType sends "notKnown"
      this.registrationForm.get('patientHealthProfile.sleepTestType').value,
      getValue('patientHealthProfile.startTherapyGroup'),
      getNumericValue('patientHealthProfile.AHIevents'),
    );
  }

  public getUpdatedPatientInfo(): UpdatedPatientInfo {
    const form = this.registrationForm;
    const getValue = (path: string) => {
      const value = form.get(path).value;
      return this.isPopulated(value) ? value : undefined;
    };
    const getNumericValue = (path: string) => {
      const value = getValue(path);
      return value !== undefined ? Number(value) : undefined;
    };

    const patientCountry = this.registrationForm.get('patientPersonalInfo.country').value;
    const analyticsFormValue: boolean = this.registrationForm.get(
      'patientLegalConsents.analyticsMode',
    ).value;
    const analyticsMode = this.requireAnalyticsMode
      ? analyticsFormValue
        ? this.optInAnalyticsMode
        : this.optOutAnalyticsMode
      : undefined;

    const serialNumber = this.userRegistrationService.serialNumber;
    const deviceNumber = this.userRegistrationService.deviceNumber;

    return new UpdatedPatientInfo(
      !!this.registrationForm.get('patientLegalConsents.termsOfUseConsent').value,
      this.getFormattedBirthDayForAutobahn(),
      (this.registrationForm.get('patientPersonalInfo.signUpGroup.email').value as string).trim(),
      patientCountry,
      this.languageService.getCurrent2LettersLanguage(),
      (this.registrationForm.get('patientPersonalInfo.lastName').value as string).trim(),
      (this.registrationForm.get('patientPersonalInfo.firstName').value as string).trim(),
      this.registrationForm.get('patientPersonalInfo.gender').value,
      this.registrationForm.get('patientPersonalInfo.timeZone').value,
      !!this.registrationForm.get('patientLegalConsents.keepMeInformed').value,
      !!this.registrationForm.get('patientLegalConsents.shareDetailsWithProviderOptIn').value,
      serialNumber?.toString(),
      deviceNumber?.toString(),
      getValue('patientPersonalInfo.furiganaFamilyName')?.trim(),
      getValue('patientPersonalInfo.furiganaGivenName')?.trim(),
      analyticsMode,
      !!getValue('patientLegalConsents.processHealthDataOptIn'),
      this.registrationForm.get('patientHealthProfile.sleepTestType').value,
      getValue('patientHealthProfile.startTherapyGroup'),
      getNumericValue('patientHealthProfile.AHIevents'),
    );
  }

  private getFormattedBirthDayForAutobahn(): string {
    const birthYear = this.registrationForm.get('patientPersonalInfo.birthday.year').value;
    const birthMonth = this.registrationForm
      .get('patientPersonalInfo.birthday.month')
      .value.split(': ')[0]; // Extract numeric month (e.g., "1")
    const birthDay = this.registrationForm
      .get('patientPersonalInfo.birthday.day')
      .value.split(': ')[1]; // Extract numeric day (e.g., "1")
    const date = `${this.dayJSService
      .dayJS(birthYear, 'YYYY', true)
      .format('YYYY')}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}T00:00:00`; // Proper ISO 8601 format
    return date;
  }

  private getFormattedBirthDay(): string {
    const birthYear = this.registrationForm.get('patientPersonalInfo.birthday.year').value;
    const birthMonth = this.registrationForm.get('patientPersonalInfo.birthday.month').value;
    const birthDay = this.registrationForm.get('patientPersonalInfo.birthday.day').value;
    const date = `${this.dayJSService
      .dayJS(birthYear, 'YYYY', true)
      .format('YYYY')}-${this.dayJSService
      .dayJS(birthMonth, 'MMM')
      .format('MM')}-${birthDay.toString().padStart(2, '0')}T00:00:00`;
    return date;
  }
}
